<template>
  <div class="dash">
    <!-- ── Page header + period filter ─────────────────────────────── -->
    <header class="dash-hero">
      <div class="dash-hero-text">
        <span class="dash-eyebrow">{{ isDriver ? "Espace livreur" : "Responsable de zone" }}</span>
        <h1 class="dash-title">Bonjour{{ firstName ? `, ${firstName}` : "" }}</h1>
        <p class="dash-sub">
          {{ todayLabel }}
          <template v-if="lastUpdated"> · Mis à jour à {{ lastUpdated }}</template>
        </p>
      </div>

      <div class="dash-period">
        <div class="dash-segment" role="group" aria-label="Période">
          <button
            v-for="option in periodOptions"
            :key="option.key"
            type="button"
            class="dash-segment-btn"
            :class="{ active: period === option.key }"
            :aria-pressed="period === option.key"
            @click="setPeriod(option.key)"
          >
            {{ option.label }}
          </button>
        </div>
        <button
          type="button"
          class="ui-icon-btn dash-refresh"
          :class="{ spinning: isLoader }"
          aria-label="Actualiser"
          title="Actualiser"
          :disabled="isLoader"
          @click="refreshAll"
        >
          <i class="ph ph-arrows-clockwise" aria-hidden="true"></i>
        </button>
      </div>
    </header>

    <!-- refunds assigned to the driver: scrolling banner, click opens the list -->
    <router-link v-if="isDriver && refundAlert.count" to="/refunds" class="dash-refund-alert" role="alert">
      <span class="dash-refund-badge">
        <i class="ph ph-hand-coins" aria-hidden="true"></i>
        {{ refundAlert.count }} remboursement{{ refundAlert.count > 1 ? "s" : "" }}
        {{ refundAlert.count > 1 ? "affectés" : "affecté" }}
      </span>
      <span class="dash-refund-window">
        <span class="dash-refund-track">
          <span v-for="n in 2" :key="n" class="dash-refund-items" :aria-hidden="n === 2">
            <span v-for="item in refundAlert.items" :key="item.id" class="dash-refund-item">
              <b>{{ item.ref }}</b> · {{ item.receiver }}<template v-if="item.city_name"> ({{ item.city_name }})</template>
              · {{ item.amount }} {{ refundAlert.currency }}
              <template v-if="item.scheduled_for"> · prévu le {{ refundDate(item.scheduled_for) }}</template>
              <em v-if="item.status === 'ASSIGNED'"> · à accepter</em>
            </span>
          </span>
        </span>
      </span>
      <i class="ph ph-caret-right dash-refund-go" aria-hidden="true"></i>
    </router-link>

    <transition name="dash-slide">
      <form v-if="period === 'custom'" class="dash-range" @submit.prevent="FilterData">
        <label class="dash-range-field">
          <span>Du</span>
          <input v-model="searchSDate" type="date" class="form-control" :max="searchEDate || undefined" required />
        </label>
        <label class="dash-range-field">
          <span>Au</span>
          <input v-model="searchEDate" type="date" class="form-control" :min="searchSDate || undefined" required />
        </label>
        <button type="submit" class="btn btn-primary" :disabled="isLoader">Appliquer</button>
      </form>
    </transition>

    <div v-if="loadError" class="dash-empty">
      <i class="ph ph-cloud-slash" aria-hidden="true"></i>
      <p>Aucune donnée disponible.</p>
    </div>

    <template v-else>
      <!-- ── Driver: delivery-rate hero + KPIs ─────────────────────── -->
      <section v-if="isDriver" class="drv-hero">
        <article class="rate-card">
          <div class="rate-ring" role="img" :aria-label="`Taux de livraison ${rate}%`">
            <svg viewBox="0 0 120 120">
              <circle class="rate-track" cx="60" cy="60" r="50" />
              <circle
                class="rate-value"
                cx="60"
                cy="60"
                r="50"
                :style="{ strokeDashoffset: ringOffset }"
              />
            </svg>
            <span class="rate-label">
              <b><AnimatedNumber :value="rate" /><small>%</small></b>
              <span>livrés</span>
            </span>
          </div>
          <div class="rate-text">
            <span class="kpi-label">Votre taux de livraison</span>
            <p>{{ rateMessage }}</p>
          </div>
        </article>

        <div class="kpi-stack">
          <article class="kpi" :class="{ 'is-loading': isLoader }">
            <div class="kpi-top">
              <span class="kpi-label">Total colis</span>
              <span class="kpi-icon"><i class="ph ph-package" aria-hidden="true"></i></span>
            </div>
            <div class="kpi-value"><AnimatedNumber :value="num(dashData.totalParcels)" /></div>
            <p class="kpi-foot">{{ periodLabel }}</p>
          </article>
          <article class="kpi kpi-success" :class="{ 'is-loading': isLoader }">
            <div class="kpi-top">
              <span class="kpi-label">Colis livrés</span>
              <span class="kpi-icon"><i class="ph ph-check-circle" aria-hidden="true"></i></span>
            </div>
            <div class="kpi-value"><AnimatedNumber :value="num(dashData.totalParcels_livred)" /></div>
            <p class="kpi-foot">Livraisons réussies</p>
          </article>
          <article class="kpi kpi-warning" :class="{ 'is-loading': isLoader }">
            <div class="kpi-top">
              <span class="kpi-label">Reportés aujourd'hui</span>
              <span class="kpi-icon"><i class="ph ph-clock-countdown" aria-hidden="true"></i></span>
            </div>
            <div class="kpi-value"><AnimatedNumber :value="num(dashData.POSTPONED)" /></div>
            <p class="kpi-foot">À reprogrammer</p>
          </article>
        </div>
      </section>

      <!-- ── Zone manager KPIs ──────────────────────────────────────── -->
      <section v-else class="kpi-grid" aria-label="Indicateurs clés">
        <article class="kpi kpi-featured" :class="{ 'is-loading': isLoader }">
          <div class="kpi-top">
            <span class="kpi-label">Total colis</span>
            <span class="kpi-icon"><i class="ph ph-package" aria-hidden="true"></i></span>
          </div>
          <div class="kpi-value"><AnimatedNumber :value="num(dashData.totalParcels)" /></div>
          <p class="kpi-foot">{{ periodLabel }}</p>
        </article>
        <article class="kpi kpi-success" :class="{ 'is-loading': isLoader }">
          <div class="kpi-top">
            <span class="kpi-label">Livrés</span>
            <span class="kpi-icon"><i class="ph ph-check-circle" aria-hidden="true"></i></span>
          </div>
          <div class="kpi-value"><AnimatedNumber :value="statusNum('DELIVERED')" /></div>
          <div class="kpi-progress" :title="`Taux de livraison ${zoneRate}%`">
            <span :style="{ width: zoneRate + '%' }"></span>
          </div>
          <p class="kpi-foot"><b>{{ zoneRate }}%</b> taux de livraison</p>
        </article>
        <article class="kpi kpi-info" :class="{ 'is-loading': isLoader }">
          <div class="kpi-top">
            <span class="kpi-label">En cours</span>
            <span class="kpi-icon"><i class="ph ph-truck" aria-hidden="true"></i></span>
          </div>
          <div class="kpi-value"><AnimatedNumber :value="statusNum('IN_PROGRESS') + statusNum('DISTRIBUTION')" /></div>
          <p class="kpi-foot">En cours et en distribution</p>
        </article>
        <article class="kpi kpi-danger" :class="{ 'is-loading': isLoader }">
          <div class="kpi-top">
            <span class="kpi-label">Retournés et refusés</span>
            <span class="kpi-icon"><i class="ph ph-arrow-u-up-left" aria-hidden="true"></i></span>
          </div>
          <div class="kpi-value"><AnimatedNumber :value="statusNum('RETURNED') + statusNum('REFUSE')" /></div>
          <p class="kpi-foot">{{ fmt(statusNum("RETURNED")) }} retournés · {{ fmt(statusNum("REFUSE")) }} refusés</p>
        </article>
      </section>

      <!-- ── Status strip ───────────────────────────────────────────── -->
      <section class="dash-section">
        <div class="dash-section-head">
          <div>
            <span class="dash-eyebrow">Suivi</span>
            <h2 class="dash-section-title">Colis par statut</h2>
          </div>
        </div>
        <div class="status-grid">
          <article
            v-for="status in statuses"
            :key="status.key"
            class="status-tile"
            :style="{ '--st': status.color }"
          >
            <span class="status-bar" aria-hidden="true"></span>
            <span class="status-name">{{ status.name }}</span>
            <span class="status-num"><AnimatedNumber :value="status.num" /></span>
            <span class="status-share">{{ status.share }}% du total</span>
          </article>
        </div>
      </section>

      <!-- ── 9-day trend ────────────────────────────────────────────── -->
      <section class="dash-panel">
        <header class="dash-panel-head">
          <div>
            <span class="dash-eyebrow">Tendance</span>
            <h3 class="dash-panel-title">Évolution des colis</h3>
            <p class="dash-panel-sub">9 derniers jours</p>
          </div>
          <span class="dash-chip">{{ fmt(trendTotal) }} colis</span>
        </header>
        <!-- mount the chart only once data exists, so it is never torn down mid-render
             (ApexCharts throws "Element not found" when its element disappears) -->
        <apexchart v-if="hasTrend" type="area" height="260" :options="trendOptions" :series="trendSeries" />
        <AppLoader v-else-if="isLoader" label="Chargement de la tendance…" />
        <div v-else class="dash-empty">
          <p>Aucune donnée de tendance.</p>
        </div>
      </section>

      <!-- ── Payment notes ──────────────────────────────────────────── -->
      <section v-for="group in paymentGroups" :key="group.title" class="dash-section">
        <div class="dash-section-head">
          <div>
            <span class="dash-eyebrow">Paiements</span>
            <h2 class="dash-section-title">{{ group.title }}</h2>
          </div>
        </div>
        <div class="pay-grid">
          <article v-for="card in group.cards" :key="card.title" class="pay-card" :class="`tone-${card.tone}`">
            <header class="pay-head">
              <span class="pay-icon"><i :class="card.icon" aria-hidden="true"></i></span>
              <div>
                <h3 class="pay-title">{{ card.title }}</h3>
                <span class="pay-count"><AnimatedNumber :value="num(card.notes)" /> bons</span>
              </div>
            </header>
            <dl class="pay-rows">
              <template v-for="row in card.rows" :key="row.label">
                <dt>{{ row.label }}</dt>
                <dd>{{ row.unit === "colis" ? `${fmt(row.value)} colis` : `${money(row.value)} ${devise}` }}</dd>
              </template>
            </dl>
            <div class="pay-total">
              <span>Total</span>
              <b>{{ money(card.total) }} <small>{{ devise }}</small></b>
            </div>
          </article>
        </div>
      </section>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, ref } from "vue";
import api from "@/utils/axios";
import { useAuthStore } from "@/stores/auth";
import AnimatedNumber from "@/components/Dashboard/AnimatedNumber.vue";

// Dashboard for the field roles (same endpoints as the former DashboardDriver /
// DashboardModera): StatisticParcels (+ TauxLivreur for drivers).
export default defineComponent({
  name: "FieldDashboard",
  components: { AnimatedNumber },
  props: {
    role: { type: String, default: "driver" }, // "driver" | "zone"
  },
  setup(props) {
    const auth = useAuthStore();
    const user = auth.user as any;
    const isDriver = computed(() => props.role === "driver");

    const dashData = ref<any>({});
    // default period: today
    const pad = (n: number) => String(n).padStart(2, "0");
    const now0 = new Date();
    const todayStr = `${now0.getFullYear()}-${pad(now0.getMonth() + 1)}-${pad(now0.getDate())}`;
    const searchSDate = ref(todayStr);
    const searchEDate = ref(todayStr);
    const isLoader = ref(true); // getData() runs on mount
    const loadError = ref(false);
    const lastUpdated = ref("");
    const rate = ref(0);

    const getData = async () => {
      isLoader.value = true;
      loadError.value = false;
      try {
        const response = await api.get(
          `/api/auth/StatisticParcels?user_id=${user?.id}&searchSDate=${searchSDate.value}&searchEDate=${searchEDate.value}`
        );
        dashData.value = response.data || {};
        lastUpdated.value = new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
      } catch (error) {
        console.error(error);
        loadError.value = !Object.keys(dashData.value).length;
      } finally {
        isLoader.value = false;
      }
    };

    const getRate = async () => {
      try {
        const response = await api.get(`/api/auth/TauxLivreur`);
        rate.value = Math.round(Number(response.data?.percent) || 0);
      } catch (error) {
        console.error("Error updating series:", error);
      }
    };

    const FilterData = () => getData();
    const ClearFilterData = () => {
      period.value = "today";
      searchSDate.value = todayStr;
      searchEDate.value = todayStr;
      getData();
    };

    // ── Period filter → the existing searchSDate / searchEDate parameters ──
    const periodOptions = [
      { key: "all", label: "Tout" },
      { key: "today", label: "Aujourd'hui" },
      { key: "7d", label: "7 jours" },
      { key: "month", label: "Ce mois" },
      { key: "custom", label: "Personnalisé" },
    ];
    const period = ref("today");
    const ymd = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    function rangeFor(key: string): [string, string] {
      const now = new Date();
      const today = ymd(now);
      if (key === "today") return [today, today];
      if (key === "7d") {
        const d = new Date(now);
        d.setDate(d.getDate() - 6);
        return [ymd(d), today];
      }
      if (key === "month") return [ymd(new Date(now.getFullYear(), now.getMonth(), 1)), today];
      return ["", ""];
    }
    function setPeriod(key: string) {
      period.value = key;
      if (key === "custom") return; // applied with the "Appliquer" button
      [searchSDate.value, searchEDate.value] = rangeFor(key);
      getData();
    }
    function refreshAll() {
      getData();
      if (isDriver.value) getRate();
    }
    const periodLabel = computed(() => {
      if (period.value === "custom" && searchSDate.value) {
        const f = (s: string) => new Date(s + "T00:00:00").toLocaleDateString("fr-FR");
        return `Du ${f(searchSDate.value)} au ${f(searchEDate.value)}`;
      }
      const option = periodOptions.find((o) => o.key === period.value);
      return option && option.key !== "all" ? option.label : "Depuis le début";
    });

    // ── Formatting ──
    const num = (v: any) => {
      const n = parseFloat(String(v ?? "").replace(/\s/g, "").replace(",", "."));
      return Number.isFinite(n) ? n : 0;
    };
    const fmt = (v: any) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(num(v));
    const money = (v: any) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(num(v));
    const devise = computed(() => dashData.value?.devis?.devis ?? "");

    const firstName = computed(() => String(user?.name || "").trim().split(/\s+/)[0] || "");
    const todayLabel = (() => {
      const s = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
      return s.charAt(0).toUpperCase() + s.slice(1);
    })();

    // ── Driver delivery rate ring ──
    const CIRC = 2 * Math.PI * 50;
    const ringOffset = computed(() => CIRC * (1 - Math.min(100, Math.max(0, rate.value)) / 100));
    const rateMessage = computed(() => {
      if (rate.value >= 85) return "Excellent travail, continuez comme ça !";
      if (rate.value >= 70) return "Bon rythme, encore un petit effort.";
      if (rate.value > 0) return "Chaque livraison compte : visez plus haut.";
      return "Votre taux apparaîtra après vos premières livraisons.";
    });

    // ── Statuses (names/colors come from the API) ──
    const STATUS_KEYS: [string, string][] = [
      ["NEW_PARCEL", "Nouveaux colis"],
      ["WAITING_PICKUP", "Attente de ramassage"],
      ["PICKED_UP", "Ramassés"],
      ["DISTRIBUTION", "En distribution"],
      ["IN_PROGRESS", "En cours"],
      ["DELIVERED", "Livrés"],
      ["REFUSE", "Refusés"],
      ["RETURNED", "Retournés"],
    ];
    const DRIVER_KEYS = ["DISTRIBUTION", "IN_PROGRESS", "DELIVERED", "REFUSE", "RETURNED"];
    const statusNum = (key: string) => num(dashData.value?.ParcelsStatus?.[key]?.num);
    const statuses = computed(() => {
      const keys = STATUS_KEYS.filter(([k]) => !isDriver.value || DRIVER_KEYS.includes(k));
      const total = keys.reduce((sum, [k]) => sum + statusNum(k), 0);
      return keys.map(([key, fallback]) => {
        const s = dashData.value?.ParcelsStatus?.[key] || {};
        const n = num(s.num);
        return {
          key,
          name: s.name ?? fallback,
          color: s.color || "#A8A29E",
          num: n,
          share: total ? Math.round((n / total) * 100) : 0,
        };
      });
    });
    const zoneRate = computed(() => {
      const total = num(dashData.value?.totalParcels);
      return total ? Math.min(100, Math.round((statusNum("DELIVERED") / total) * 100)) : 0;
    });

    // ── 9-day trend (DataParcels) ──
    const trendData = computed<number[]>(() => (dashData.value?.DataParcels || []).map((v: any) => num(v)));
    const hasTrend = computed(() => trendData.value.length > 0);
    const trendTotal = computed(() => trendData.value.reduce((a, b) => a + b, 0));
    const trendSeries = computed(() => [{ name: "Colis", data: trendData.value }]);
    const trendOptions = computed(() => {
      const css = getComputedStyle(document.body);
      const grid = css.getPropertyValue("--ui-border").trim() || "#FADBC8";
      const accent = css.getPropertyValue("--ui-accent").trim() || "#FF5500";
      return {
        chart: { type: "area", fontFamily: "Inter, sans-serif", toolbar: { show: false }, zoom: { enabled: false } },
        colors: [accent],
        stroke: { width: 3, curve: "smooth" },
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 90, 100] } },
        markers: { size: 4, colors: [accent], strokeColors: "#FFFFFF", strokeWidth: 2, hover: { size: 6 } },
        dataLabels: { enabled: false },
        grid: { borderColor: grid, strokeDashArray: 4 },
        xaxis: {
          categories: ["J-8", "J-7", "J-6", "J-5", "J-4", "J-3", "J-2", "J-1", "Aujourd'hui"],
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { style: { colors: "#78716C", fontWeight: 600 } },
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: { style: { colors: "#78716C" }, formatter: (val: number) => Math.round(val).toString() },
        },
        tooltip: { y: { formatter: (val: number) => `${val} Colis` } },
      };
    });

    // ── Payment notes ──
    const paymentGroups = computed(() => {
      const d = dashData.value || {};
      const driverCards = [
        {
          title: "Payés",
          tone: "success",
          icon: "ph ph-check-circle",
          notes: d.totalNotePaid,
          rows: [
            { label: "Colis", value: d.payn_l_parcels, unit: "colis" },
            { label: "CRBT", value: d.payn_l_crbt },
            { label: "Frais", value: d.payn_l_fees },
          ],
          total: d.payn_l_total,
        },
        {
          title: "Non payés",
          tone: "warning",
          icon: "ph ph-hourglass",
          notes: d.totalNoteNotPaid,
          rows: [
            { label: "Colis", value: d.payn_l_not_parcels, unit: "colis" },
            { label: "CRBT", value: d.payn_l_not_crbt },
            { label: "Frais", value: d.payn_l_not_fees },
          ],
          total: d.payn_l_not_total,
        },
      ];
      if (isDriver.value) return [{ title: "Vos bons de paiement", cards: driverCards }];
      return [
        {
          title: "Bons de paiement livreurs",
          cards: [
            ...driverCards,
            {
              title: "Total",
              tone: "info",
              icon: "ph ph-stack",
              notes: d.total_l_payn,
              rows: [
                { label: "Colis", value: d.total_l_parcels, unit: "colis" },
                { label: "CRBT", value: d.total_l_crbt },
                { label: "Frais", value: d.total_l_fees },
              ],
              total: d.total_l_total,
            },
          ],
        },
        {
          title: "Bons de paiement zone",
          cards: [
            {
              title: "Payés",
              tone: "success",
              icon: "ph ph-check-circle",
              notes: d.totalZNotePaid,
              rows: [
                { label: "Colis", value: d.payn_z_parcels, unit: "colis" },
                { label: "Frais", value: d.payn_z_fees },
              ],
              total: d.payn_z_total,
            },
            {
              title: "Non payés",
              tone: "warning",
              icon: "ph ph-hourglass",
              notes: d.totalZNoteNotPaid,
              rows: [
                { label: "Colis", value: d.payn_z_not_parcels, unit: "colis" },
                { label: "Frais", value: d.payn_z_not_fees },
              ],
              total: d.payn_z_not_total,
            },
            {
              title: "Total",
              tone: "info",
              icon: "ph ph-stack",
              notes: d.total_z_payn,
              rows: [
                { label: "Colis", value: d.total_z_parcels, unit: "colis" },
                { label: "Frais", value: d.total_z_fees },
              ],
              total: d.total_z_total,
            },
          ],
        },
      ];
    });

    // ── Refunds assigned to the driver (banner), refreshed every minute ──
    const refundAlert = ref<any>({ count: 0, items: [], currency: "DH" });
    const refundDate = (iso: string) => String(iso).slice(0, 10).split("-").reverse().join("/");
    const getRefundAlert = async () => {
      try {
        const response = await api.get(`/api/auth/refunds/attention`);
        refundAlert.value = response.data || refundAlert.value;
      } catch (error) {
        // the banner is a bonus: stay silent
      }
    };
    let refundTimer: ReturnType<typeof setInterval> | undefined;

    onMounted(() => {
      getData();
      if (isDriver.value) {
        getRate();
        getRefundAlert();
        refundTimer = setInterval(getRefundAlert, 60000);
      }
    });
    onBeforeUnmount(() => clearInterval(refundTimer));

    return {
      refundAlert,
      refundDate,
      isDriver,
      dashData,
      searchSDate,
      searchEDate,
      isLoader,
      loadError,
      lastUpdated,
      getData,
      FilterData,
      ClearFilterData,
      periodOptions,
      period,
      setPeriod,
      refreshAll,
      periodLabel,
      num,
      fmt,
      money,
      devise,
      firstName,
      todayLabel,
      rate,
      ringOffset,
      rateMessage,
      statuses,
      statusNum,
      zoneRate,
      hasTrend,
      trendTotal,
      trendSeries,
      trendOptions,
      paymentGroups,
    };
  },
});
</script>


<style scoped>
/* All values come from the design tokens (assets/css/theme/_tokens.scss).
   Shared panel primitives live in assets/css/theme/_dashboard.scss. */
.dash {
  display: flex;
  flex-direction: column;
  gap: var(--ui-space-6);
  padding-bottom: var(--ui-space-6);
}

/* ── Refunds assigned to the driver (scrolling banner) ─────────── */
.dash-refund-alert {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: var(--ui-space-4, 16px);
  padding: 10px 14px;
  border: 1px solid var(--ui-warning);
  border-radius: 12px;
  background: var(--ui-warning-soft);
  color: var(--ui-warning-strong);
  text-decoration: none;
  overflow: hidden;
}
.dash-refund-alert:hover {
  color: var(--ui-warning-strong);
  box-shadow: 0 0 0 3px rgba(var(--ui-warning-rgb), 0.18);
}
.dash-refund-badge {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--ui-warning);
  color: #fff;
  font-weight: 700;
  font-size: 0.82rem;
  white-space: nowrap;
}
.dash-refund-window {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
}
.dash-refund-track {
  display: inline-flex;
  width: max-content;
  animation: dash-refund-scroll 22s linear infinite;
}
.dash-refund-alert:hover .dash-refund-track {
  animation-play-state: paused;
}
.dash-refund-items {
  display: inline-flex;
  flex: 0 0 auto;
}
.dash-refund-item {
  padding-right: 48px;
  font-size: 0.9rem;
  font-weight: 600;
}
.dash-refund-item em {
  font-style: normal;
  font-weight: 800;
  text-transform: uppercase;
}
.dash-refund-go {
  flex: 0 0 auto;
}
@keyframes dash-refund-scroll {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}
@media (prefers-reduced-motion: reduce) {
  .dash-refund-track {
    animation: none;
    flex-wrap: wrap;
    white-space: normal;
  }
  .dash-refund-items:last-child {
    display: none;
  }
  .dash-refund-window {
    white-space: normal;
  }
}

/* ── Header ─────────────────────────────────────────────────────── */
.dash-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--ui-space-4);
  flex-wrap: wrap;
  padding-bottom: var(--ui-space-5);
  border-bottom: 1px solid var(--ui-border);
}
.dash-title {
  margin: 0;
  color: var(--ui-text);
  font-size: clamp(1.75rem, 3vw, 2.4rem);
  font-weight: var(--ui-fw-black);
  letter-spacing: -0.02em;
  line-height: 1.1;
}
.dash-sub {
  margin: 6px 0 0;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-semibold);
}
.dash-period {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 100%;
}
.dash-segment {
  display: flex;
  gap: 4px;
  max-width: 100%;
  padding: 4px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
  overflow-x: auto;
  scrollbar-width: none;
}
.dash-segment::-webkit-scrollbar {
  display: none;
}
.dash-segment-btn {
  flex: 0 0 auto;
  padding: 8px 14px;
  border: 0;
  border-radius: var(--ui-radius-pill);
  background: transparent;
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
  white-space: nowrap;
  transition: background-color var(--ui-dur-fast) var(--ui-ease), color var(--ui-dur-fast) var(--ui-ease),
    box-shadow var(--ui-dur-fast) var(--ui-ease);
}
.dash-segment-btn:hover:not(.active) {
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
}
.dash-segment-btn.active {
  background: var(--ui-accent);
  color: #FFFFFF;
  box-shadow: 0 6px 16px rgba(var(--ui-accent-rgb), 0.28);
}
.dash-refresh.spinning i {
  animation: dash-spin 0.9s linear infinite;
}
@keyframes dash-spin {
  to { transform: rotate(360deg); }
}

.dash-range {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: calc(var(--ui-space-2) * -1);
}
.dash-range-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-heavy);
  text-transform: uppercase;
  letter-spacing: var(--ui-tracking-label);
}
.dash-range-field .form-control {
  min-width: 170px;
}
.dash-slide-enter-active,
.dash-slide-leave-active {
  transition: opacity var(--ui-dur) var(--ui-ease), transform var(--ui-dur) var(--ui-ease);
}
.dash-slide-enter-from,
.dash-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ── KPIs ───────────────────────────────────────────────────────── */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--ui-space-5);
}
.kpi {
  --tone: var(--ui-accent);
  --tone-soft: var(--ui-accent-soft);
  position: relative;
  display: flex;
  flex-direction: column;
  padding: var(--ui-space-5) var(--ui-space-6);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-xl);
  background:
    radial-gradient(circle at 100% 0%, var(--tone-soft), transparent 55%),
    var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
  overflow: hidden;
  transition: transform var(--ui-dur) var(--ui-ease), box-shadow var(--ui-dur) var(--ui-ease),
    border-color var(--ui-dur) var(--ui-ease);
}
.kpi:hover {
  transform: translateY(-3px);
  border-color: var(--tone);
  box-shadow: var(--ui-shadow-card-hover);
}
.kpi-success { --tone: var(--ui-success); --tone-soft: var(--ui-success-soft); }
.kpi-info { --tone: var(--ui-info); --tone-soft: var(--ui-info-soft); }
.kpi-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.kpi-label {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-black);
  letter-spacing: var(--ui-tracking-label);
  text-transform: uppercase;
}
.kpi-icon {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: var(--ui-radius-sm);
  background: var(--tone-soft);
  color: var(--tone);
  font-size: 19px;
}
.kpi-value {
  margin-top: var(--ui-space-3);
  color: var(--ui-text);
  font-size: clamp(1.7rem, 2.4vw, 2.2rem);
  font-weight: var(--ui-fw-black);
  line-height: 1.1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}
.kpi-value small {
  margin-left: 4px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-heavy);
  letter-spacing: 0;
}
.kpi-foot {
  margin: auto 0 0;
  padding-top: var(--ui-space-2);
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-semibold);
}
.kpi-foot b {
  color: var(--tone);
  font-weight: var(--ui-fw-black);
}
.kpi-progress {
  height: 6px;
  margin-top: var(--ui-space-3);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  overflow: hidden;
}
.kpi-progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--tone);
  transition: width 800ms var(--ui-ease-spring);
}
.kpi-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  margin: 0 5px 1px 0;
  border-radius: 50%;
}
.kpi-foot .kpi-dot:not(:first-child) {
  margin-left: 10px;
}
.kpi-dot.danger { background: var(--ui-danger); }
.kpi-dot.warning { background: var(--ui-warning); }

/* Featured KPI: solid accent card */
.kpi-featured {
  border-color: var(--ui-accent-strong);
  background:
    radial-gradient(circle at 110% -10%, rgba(255, 255, 255, 0.22) 0 35%, transparent 36%),
    radial-gradient(circle at -10% 120%, rgba(0, 0, 0, 0.1) 0 40%, transparent 41%),
    linear-gradient(145deg, var(--ui-accent-light) 0%, var(--ui-accent) 50%, var(--ui-accent-strong) 100%);
  box-shadow: var(--ui-shadow-accent);
}
.kpi-featured:hover {
  border-color: var(--ui-accent-strong);
  box-shadow: 0 16px 36px rgba(var(--ui-accent-rgb), 0.4);
}
.kpi-featured .kpi-label,
.kpi-featured .kpi-foot,
.kpi-featured .kpi-value small {
  color: rgba(255, 255, 255, 0.85);
}
.kpi-featured .kpi-value {
  color: #FFFFFF;
}
.kpi-featured .kpi-icon {
  background: rgba(255, 255, 255, 0.2);
  color: #FFFFFF;
}

/* Loading shimmer over the values */
.kpi.is-loading .kpi-value,
.tile-grid .tile-value {
  position: relative;
}
.kpi.is-loading .kpi-value::after {
  content: "";
  position: absolute;
  inset: 4px 30% 4px 0;
  border-radius: var(--ui-radius-xs);
  background: linear-gradient(90deg, var(--ui-surface-sunken), var(--ui-surface-hover), var(--ui-surface-sunken));
  background-size: 300% 100%;
  animation: dash-shimmer 1.2s ease-in-out infinite;
}
.kpi-featured.is-loading .kpi-value::after {
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.35), rgba(255, 255, 255, 0.15));
  background-size: 300% 100%;
}
.kpi.is-loading .kpi-value > * {
  opacity: 0;
}
@keyframes dash-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

/* ── Activity tiles ─────────────────────────────────────────────── */
.tile-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: var(--ui-space-4);
}
.tile {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  color: inherit;
  text-decoration: none;
  transition: transform var(--ui-dur) var(--ui-ease), border-color var(--ui-dur) var(--ui-ease),
    box-shadow var(--ui-dur) var(--ui-ease);
}
.tile.clickable:hover {
  transform: translateY(-2px);
  border-color: var(--ui-accent);
  box-shadow: var(--ui-shadow-card-hover);
}
.tile-icon {
  --tone: var(--ui-accent);
  --tone-soft: var(--ui-accent-soft);
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--tone-soft);
  color: var(--tone);
  font-size: 19px;
}
.tone-success { --tone: var(--ui-success); --tone-soft: var(--ui-success-soft); }
.tone-warning { --tone: var(--ui-warning-strong); --tone-soft: var(--ui-warning-soft); }
.tone-danger { --tone: var(--ui-danger); --tone-soft: var(--ui-danger-soft); }
.tone-info { --tone: var(--ui-info); --tone-soft: var(--ui-info-soft); }
.tile-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.tile-value {
  color: var(--ui-text);
  font-size: var(--ui-fs-lg);
  font-weight: var(--ui-fw-black);
  line-height: 1.1;
}
.tile-label {
  overflow: hidden;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-semibold);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tile-go {
  position: absolute;
  top: 10px;
  right: 10px;
  color: var(--ui-text-faint);
  font-size: 13px;
  transition: color var(--ui-dur-fast) var(--ui-ease), transform var(--ui-dur-fast) var(--ui-ease);
}
.tile.clickable:hover .tile-go {
  color: var(--ui-accent-text);
  transform: translate(2px, -2px);
}

/* ── Sections ───────────────────────────────────────────────────── */
.dash-section-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--ui-space-3);
  flex-wrap: wrap;
  margin-bottom: var(--ui-space-4);
}
.dash-section-title {
  margin: 0;
  color: var(--ui-text);
  font-size: var(--ui-fs-lg);
  font-weight: var(--ui-fw-black);
}
.dash-section-hint {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
}

/* Outcome cards */
.outcome-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--ui-space-5);
}
.outcome {
  --tone: var(--ui-accent);
  --tone-soft: var(--ui-accent-soft);
  display: flex;
  flex-direction: column;
  padding: var(--ui-space-5);
  border: 1px solid var(--ui-border);
  border-top: 4px solid var(--tone);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
}
.outcome-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: var(--ui-space-4);
}
.outcome-icon {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: var(--ui-radius-md);
  background: var(--tone-soft);
  color: var(--tone);
  font-size: 21px;
}
.outcome-title {
  margin: 0;
  color: var(--ui-text);
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-black);
}
.outcome-count {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
}
.outcome-split {
  margin-bottom: var(--ui-space-4);
}
.outcome-split-bar {
  height: 8px;
  border-radius: var(--ui-radius-pill);
  background: var(--tone-soft);
  overflow: hidden;
}
.outcome-split-bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--tone);
  transition: width 800ms var(--ui-ease-spring);
}
.outcome-split-legend {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-bold);
}
.outcome-split-legend .dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  margin-right: 5px;
  border-radius: 50%;
}
.outcome-split-legend .dot.solid { background: var(--tone); }
.outcome-split-legend .dot.soft { background: var(--tone-soft); outline: 1px solid var(--tone); }
.outcome-rows {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px 12px;
  margin: 0;
}
.outcome-rows dt {
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-medium);
}
.outcome-rows dd {
  margin: 0;
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-heavy);
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.outcome-total {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-top: auto;
  padding-top: var(--ui-space-4);
  border-top: 1px dashed var(--ui-border-strong);
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
}
.outcome-rows + .outcome-total {
  margin-top: var(--ui-space-4);
}
.outcome-total b {
  color: var(--tone);
  font-size: var(--ui-fs-lg);
  font-weight: var(--ui-fw-black);
  font-variant-numeric: tabular-nums;
}
.outcome-total small {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
}

/* Charts */
.chart-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  gap: var(--ui-space-5);
}

/* Welcome (no stats permission) */
.dash-welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 24px;
  text-align: center;
}
.dash-welcome-icon {
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
  border-radius: 50%;
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
  font-size: 30px;
}
.dash-welcome h2 {
  margin: 0 0 6px;
  font-size: var(--ui-fs-xl);
  font-weight: var(--ui-fw-black);
}
.dash-welcome p {
  margin: 0;
  color: var(--ui-text-muted);
}

/* ── Announcements ──────────────────────────────────────────────── */
.notice-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.notice-card {
  --notice-accent: var(--ui-info);
  --notice-soft: var(--ui-info-soft);
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  background:
    linear-gradient(90deg, var(--notice-soft), transparent 40%),
    var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
}
.notice-card-success { --notice-accent: var(--ui-success); --notice-soft: var(--ui-success-soft); }
.notice-card-danger { --notice-accent: var(--ui-danger); --notice-soft: var(--ui-danger-soft); }
.notice-card-warning { --notice-accent: var(--ui-warning-strong); --notice-soft: var(--ui-warning-soft); }
.notice-card-info,
.notice-card-primary { --notice-accent: var(--ui-accent-text); --notice-soft: var(--ui-accent-soft); }
.notice-card-icon {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: var(--notice-soft);
  color: var(--notice-accent);
  font-size: 16px;
}
.notice-card-body {
  flex: 1 1 auto;
  color: var(--ui-text);
  font-size: var(--ui-fs-base);
}
.notice-card-body :deep(p) {
  margin: 0;
}
.notice-card-close {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--ui-text-muted);
  font-size: 15px;
  transition: background-color var(--ui-dur-fast) var(--ui-ease), color var(--ui-dur-fast) var(--ui-ease);
}
.notice-card-close:hover {
  background: var(--ui-surface-sunken);
  color: var(--ui-text);
}
.notice-fade-enter-active,
.notice-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.notice-fade-enter-from,
.notice-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ── Entrance animation (staggered) ─────────────────────────────── */
.kpi,
.tile,
.outcome {
  animation: dash-rise 520ms var(--ui-ease-spring) both;
}
.kpi:nth-child(2), .outcome:nth-child(2), .tile:nth-child(2) { animation-delay: 60ms; }
.kpi:nth-child(3), .outcome:nth-child(3), .tile:nth-child(3) { animation-delay: 120ms; }
.kpi:nth-child(4), .outcome:nth-child(4), .tile:nth-child(4) { animation-delay: 180ms; }
.tile:nth-child(5) { animation-delay: 240ms; }
.tile:nth-child(6) { animation-delay: 300ms; }
@keyframes dash-rise {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: none; }
}

/* ── Responsive ─────────────────────────────────────────────────── */
@media (max-width: 1399.98px) {
  .tile-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .outcome-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 1199.98px) {
  .kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .chart-grid { grid-template-columns: minmax(0, 1fr); }
  .dash-period { width: 100%; }
  .dash-segment { flex: 1 1 auto; }
}
@media (max-width: 767.98px) {
  .tile-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .outcome-grid { grid-template-columns: minmax(0, 1fr); }
  .dash-range { justify-content: stretch; }
  .dash-range-field { flex: 1 1 140px; }
  .dash-range-field .form-control { min-width: 0; }
  .dash-range .btn { flex: 1 1 100%; }
}
@media (max-width: 479.98px) {
  .kpi-grid { grid-template-columns: minmax(0, 1fr); }
  .kpi { padding: var(--ui-space-4) var(--ui-space-5); }
}
@media (prefers-reduced-motion: reduce) {
  .kpi, .tile, .outcome { animation: none; }
  .dash-refresh.spinning i { animation: none; }
}

/* ── Field dashboard additions (driver ring, statuses, payments) ── */
.kpi-warning { --tone: var(--ui-warning-strong); --tone-soft: var(--ui-warning-soft); }
.kpi-danger { --tone: var(--ui-danger); --tone-soft: var(--ui-danger-soft); }

.drv-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 2fr);
  gap: var(--ui-space-5);
}
.kpi-stack {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--ui-space-5);
}
.rate-card {
  display: flex;
  align-items: center;
  gap: var(--ui-space-5);
  padding: var(--ui-space-6);
  border: 1px solid var(--ui-accent-strong);
  border-radius: var(--ui-radius-xl);
  color: #FFFFFF;
  background:
    radial-gradient(circle at 110% -10%, rgba(255, 255, 255, 0.22) 0 35%, transparent 36%),
    radial-gradient(circle at -10% 120%, rgba(0, 0, 0, 0.1) 0 40%, transparent 41%),
    linear-gradient(145deg, var(--ui-accent-light) 0%, var(--ui-accent) 50%, var(--ui-accent-strong) 100%);
  box-shadow: var(--ui-shadow-accent);
  animation: dash-rise 520ms var(--ui-ease-spring) both;
}
.rate-ring {
  position: relative;
  flex: 0 0 auto;
  width: 132px;
  height: 132px;
}
.rate-ring svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}
.rate-ring circle {
  fill: none;
  stroke-width: 11;
}
.rate-track {
  stroke: rgba(255, 255, 255, 0.25);
}
.rate-value {
  stroke: #FFFFFF;
  stroke-linecap: round;
  stroke-dasharray: 314.16;
  transition: stroke-dashoffset 1s var(--ui-ease-spring);
}
.rate-label {
  position: absolute;
  inset: 0;
  display: grid;
  place-content: center;
  text-align: center;
  line-height: 1.05;
}
.rate-label b {
  font-size: 30px;
  font-weight: var(--ui-fw-black);
  font-variant-numeric: tabular-nums;
}
.rate-label small {
  font-size: 15px;
}
.rate-label > span {
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-bold);
  opacity: 0.85;
}
.rate-card .kpi-label {
  color: rgba(255, 255, 255, 0.85);
}
.rate-text p {
  margin: 6px 0 0;
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-heavy);
  line-height: 1.35;
}

/* Status strip */
.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: var(--ui-space-4);
}
.status-tile {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 16px 16px 14px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  overflow: hidden;
  animation: dash-rise 520ms var(--ui-ease-spring) both;
  transition: transform var(--ui-dur) var(--ui-ease), box-shadow var(--ui-dur) var(--ui-ease);
}
.status-tile:hover {
  transform: translateY(-2px);
  box-shadow: var(--ui-shadow-card-hover);
}
.status-bar {
  position: absolute;
  inset: 0 0 auto 0;
  height: 4px;
  background: var(--st);
}
.status-name {
  overflow: hidden;
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-heavy);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.status-num {
  color: var(--ui-text);
  font-size: 1.6rem;
  font-weight: var(--ui-fw-black);
  line-height: 1.15;
}
.status-share {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-semibold);
}

/* Payment cards */
.pay-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: var(--ui-space-5);
}
.pay-card {
  --tone: var(--ui-accent);
  --tone-soft: var(--ui-accent-soft);
  display: flex;
  flex-direction: column;
  padding: var(--ui-space-5);
  border: 1px solid var(--ui-border);
  border-top: 4px solid var(--tone);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
  animation: dash-rise 520ms var(--ui-ease-spring) both;
}
.pay-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: var(--ui-space-4);
}
.pay-icon {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: var(--ui-radius-md);
  background: var(--tone-soft);
  color: var(--tone);
  font-size: 21px;
}
.pay-title {
  margin: 0;
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-black);
}
.pay-count {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
}
.pay-rows {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px 12px;
  margin: 0;
}
.pay-rows dt {
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-medium);
}
.pay-rows dd {
  margin: 0;
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-heavy);
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.pay-total {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-top: var(--ui-space-4);
  padding-top: var(--ui-space-4);
  border-top: 1px dashed var(--ui-border-strong);
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
}
.pay-total b {
  color: var(--tone);
  font-size: var(--ui-fs-lg);
  font-weight: var(--ui-fw-black);
  font-variant-numeric: tabular-nums;
}
.pay-total small {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
}
.dash-refresh:disabled {
  cursor: progress;
}

@media (max-width: 1199.98px) {
  .drv-hero {
    grid-template-columns: minmax(0, 1fr);
  }
}
@media (max-width: 767.98px) {
  .kpi-stack {
    grid-template-columns: minmax(0, 1fr);
  }
  .status-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .rate-card {
    flex-direction: column;
    text-align: center;
  }
}
@media (prefers-reduced-motion: reduce) {
  .rate-card, .status-tile, .pay-card { animation: none; }
  .rate-value { transition: none; }
}
</style>
